<script setup lang="ts">
import { mdiEmoticonHappyOutline, mdiEmoticonNeutralOutline, mdiEmoticonSadOutline } from '@mdi/js'
import type { RatingTarget } from '~/types/interfaces'

/**
 * 答えの評価ボタン 👍/🤔/👎 (#654)。押すと target を /api/v1/ratings に送り、「ご回答ありがとうございました」に差し替える。
 *
 * イベントダイアログの本文（EventBody）と、羽根筆のパネルに載った相談の答え（#954）の両方で使う。
 * 背景色は使う側が `--rating-bg` で決める。
 */
const props = defineProps<{ target: RatingTarget }>()

const api = useApi()

type RatingValue = 'helpful' | 'difficult' | 'unhelpful'
const submittedRating = ref<RatingValue | null>(null)
const submittingRating = ref<RatingValue | null>(null)

const ratingOptions: { value: RatingValue, label: string, icon: string }[] = [
  { value: 'helpful', label: '役に立った', icon: mdiEmoticonHappyOutline },
  { value: 'difficult', label: 'むずかしかった', icon: mdiEmoticonNeutralOutline },
  { value: 'unhelpful', label: '役に立たなかった', icon: mdiEmoticonSadOutline },
]

watch(
  () => props.target,
  () => {
    submittedRating.value = null
    submittingRating.value = null
  },
)

const onRating = async (rating: RatingValue) => {
  if (submittedRating.value || submittingRating.value) return
  submittingRating.value = rating
  try {
    await api.submitUserRating(props.target.targetType, props.target.targetId, rating)
    submittedRating.value = rating
  }
  catch (e) {
    console.error('[UserRating] submitUserRating failed', e)
  }
  finally {
    submittingRating.value = null
  }
}
</script>

<template>
  <div
    v-if="!submittedRating"
    class="rating-buttons"
  >
    <v-tooltip
      v-for="opt in ratingOptions"
      :key="opt.value"
      location="top"
      :text="opt.label"
    >
      <template #activator="{ props: tipProps }">
        <v-btn
          v-bind="tipProps"
          :data-testid="`user-rating-${opt.value}`"
          :aria-label="opt.label"
          :icon="opt.icon"
          variant="text"
          density="comfortable"
          size="small"
          :loading="submittingRating === opt.value"
          :disabled="!!submittingRating"
          @click="onRating(opt.value)"
        />
      </template>
    </v-tooltip>
  </div>
  <span
    v-else
    class="rating-thanks"
  >
    ご回答ありがとうございました
  </span>
</template>

<style scoped>
.rating-thanks {
  color: #707080;
  font-size: max(10px, calc(1.2 * var(--u)));
  background: var(--rating-bg, transparent);
  padding: 0 max(4px, calc(0.5 * var(--u)));
  border-radius: 4px;
}

.rating-buttons {
  display: flex;
  gap: max(2px, calc(0.25 * var(--u)));
  background: var(--rating-bg, transparent);
  border-radius: 999px;
  padding: max(1px, calc(0.125 * var(--u)));
}
</style>
